// A1-95 — identical reads made at the same moment share ONE request.
//
// Five components fetch the per-country summary on their own, and several
// sit on the same screen, so opening it sent the same request two or three
// times at once — each one recomputing every country's return on the server.
//
// This is not a cache: a request is shared only while it is still in flight,
// only for SHARE_WINDOW_MS after it started (a component mounting with its
// neighbours, not one arriving late, which would inherit an old request's
// timeout and an answer begun before changes this tab cannot see, such as
// another tab's), and never across a write. Every write the app sends (noteWrite) starts a
// new generation, so a read made after a write never receives an answer that
// was begun before it. Every axios client the app creates installs
// installWriteGenerations, and a write sent with fetch() goes through
// writeFetch; sharedRead.test.js fails on a client or a fetch that does not.

export const SHARE_WINDOW_MS = 2000;

let generation = 0;
const inFlight = new Map();

export const noteWrite = () => {
  generation += 1;
};

export const isWrite = (method) =>
  !["get", "head", "options"].includes(String(method || "get").toLowerCase());

// fetch() for a write: a new generation when it is sent and when it is answered.
export async function writeFetch(...args) {
  noteWrite();
  try {
    return await fetch(...args);
  } finally {
    noteWrite();
  }
}

export function shareInFlight(key, fetcher, now = Date.now) {
  const k = `${generation}|${key}`;
  const pending = inFlight.get(k);
  if (pending && now() - pending.startedAt < SHARE_WINDOW_MS) return pending.promise;
  const promise = fetcher().finally(() => {
    if (inFlight.get(k)?.promise === promise) inFlight.delete(k);
  });
  inFlight.set(k, { promise, startedAt: now() });
  return promise;
}

// For an axios instance: a write, sent or answered, starts a new generation.
// axios may run request interceptors a tick late, so a read made in the SAME
// tick as an un-awaited write can still join an older request; the bump when
// the write is answered covers every read after that.
export function installWriteGenerations(axiosClient) {
  axiosClient.interceptors.request.use((cfg) => {
    if (isWrite(cfg.method)) noteWrite();
    return cfg;
  });
  axiosClient.interceptors.response.use(
    (r) => {
      if (isWrite(r?.config?.method)) noteWrite();
      return r;
    },
    (e) => {
      if (isWrite(e?.config?.method)) noteWrite();
      return Promise.reject(e);
    },
  );
}
